import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { useApiListAssetQuery } from '../../api-new/state/content'

// Which courses have a web build, for a course's Launch: a course without
// one offers no Launch at all, and its page and Deep link dialog say how to
// get one (NOT_ON_THE_WEB; owner 2026-10-08: never a dead button).
//
// A web build is a WEB_CONTENT asset on the course -- caffeine's build
// stamps a WebGL build's bundle WEB_CONTENT -- whose current version is
// uploaded. Nothing the content area already loads says so (the course list
// carries no assets), so this is one read for the whole area: content's
// GET /api/v1/asset-meta-query/datatype:web_content, the query the viewers
// themselves run to find a course's bundles, answered for the organization
// in view (every one of its assets for a super admin). Each asset carries
// its course's nodeID.
//
// `enabled` is the caller's gate: only someone who can launch asks. Until
// the answer is in, `settled` is false and `has` answers undefined; a failed
// read settles as unknown (undefined), so a failed check never blocks a
// launch. Another organization asks again.

export const WEB_BUILD_QUERY = 'datatype:web_content'

// What the course's page and its Deep link dialog say instead of a Launch
// button when the course has no web build.
export const NOT_ON_THE_WEB = 'Not on the web yet: publish a web build from the Unity editor to launch it in a browser.'

export const useCourseWebBuilds = enabled => {
  const { state } = useAuthContext()
  const organizationID = state ? state.organizationID : undefined

  // api-new hooks have a new identity every render: read through a ref so
  // the effect runs once per organization, not once per render.
  const apiListAssetQuery = useApiListAssetQuery()
  const apiRef = useRef(apiListAssetQuery)
  apiRef.current = apiListAssetQuery

  // { key, nodeIDs }: nodeIDs is a Set, or null when the answer is unknown.
  const [answer, setAnswer] = useState(null)
  const key = enabled ? String(organizationID) : null

  useEffect(
    () => {
      if (!key) return
      let live = true
      Promise.resolve()
        .then(() => apiRef.current.get(WEB_BUILD_QUERY))
        .then(assets => (Array.isArray(assets) ? new Set(assets.map(asset => asset && asset.nodeID).filter(id => id !== undefined && id !== null)) : null))
        .catch(() => null)
        .then(nodeIDs => {
          if (live) setAnswer({ key, nodeIDs })
        })
      return () => {
        live = false
      }
    },
    [key]
  )

  const current = key && answer && answer.key === key ? answer : null
  const nodeIDs = current ? current.nodeIDs : null

  const has = useCallback(nodeID => (nodeIDs ? nodeIDs.has(nodeID) : undefined), [nodeIDs])

  return { settled: !!current, has }
}
